import {
  useMutation,
  useQueryClient,
  useQuery,
  useInfiniteQuery,
} from "@tanstack/react-query";

import { req } from "@padi/lib/api";
import { NotificationsResponse } from "../_types";

export const notificationKeys = {
  all: ["notifications"] as const,
  list: (urlParams: string) =>
    [...notificationKeys.all, "list", urlParams] as const,
  unread: () => [...notificationKeys.all, "unread"] as const,
};

export function useInfiniteNotifications() {
  return useInfiniteQuery({
    queryKey: notificationKeys.list("infinite"),
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams({
        currPage: pageParam.toString(),
        perPage: "10",
      });
      return await req<NotificationsResponse>(
        `/notifications?${params.toString()}`,
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.currPage < lastPage.totalPages
        ? lastPage.currPage + 1
        : undefined;
    },
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: async () => {
      const res = await req<{ count: number }>("/notifications/unread-count");
      return res.count;
    },
    refetchInterval: 30000,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      return await req.post(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return await req.post("/notifications/read-all");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      return await req.delete(`/notifications/${notificationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
